import React, { useState, useEffect, useCallback, useMemo } from "react";
import Loader from "../../Shared/Loader";

import "../../../css/EditAppointmentsTable.css"; 
import api from "../../../api/axiosConfig";
import ViewEditAppointmentModal from "./ViewEditAppointmentModal";
import EditClusterSlotsModal from "./EditClusterSlotsModal";
import MoveAppointmentsModal from "./MoveAppointmentsModal";
import RescheduleConfigModal from "../Helpdesk Management/RescheduleConfigModal";
import { showToast } from "../../Shared/Toaster";

// --- Interfaces (No Changes) ---
interface Appointment {
  id: string; 
  appointmentId: string;
  centerId: string;
  centerName: string;
  centerType: string;
  city: string;
  startDate: string;
  endDate: string;
  slotDesigns: Record<string, string>;
  totalSlots: number;
  appointmentsPerSlot: number;
  totalAppointments: number;
}

interface SlotClusterItem {
  _id: string;
  appointment_cluster_id: string;
  center_id: string;
  start_date: string;
  end_date: string;
  weekday_template_map: Record<string, string>;
  total_slots: number;
  appointments_per_slot?: number;
}

interface SlotClusterResponse {
  page: number;
  limit: number;
  total: number;
  items: SlotClusterItem[];
}

interface CenterDetails {
  name: string;
  centerType: string;
  city: string;
}

interface SlotTemplateResponse {
  template: {
    _id: string;
    name: string;
    created_on: string;
  };
}

const EditAppointmentsTable: React.FC = () => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);

  // 🔽 1. State for search, filters and pagination
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchAppointmentsData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = sessionStorage.getItem("accessToken");
      if (!token) throw new Error("Authentication token not found.");

      // 1. Get Clusters
      const clusterRes = await api.appointment_api.get<SlotClusterResponse>("/api/cluster/clusters", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const clusters: SlotClusterItem[] = clusterRes.data.items || [];

      const uniqueCenterIds = Array.from(new Set(clusters.map(c => c.center_id)));

      if (clusters.length === 0) {
        setAppointments([]);
        setLoading(false);
        return;
      }

      // 2. Fetch Center Details
      const centerDetailPromises = uniqueCenterIds.map(id =>
        api.center_api.get<{ success: boolean, data: CenterDetails }>(`/api/v1/centers/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      );

      const centerResponses = await Promise.all(centerDetailPromises);

      const centerDataMap = new Map<string, CenterDetails>();
      centerResponses.forEach((res, i) => {
        if (res.data?.success) {
          centerDataMap.set(uniqueCenterIds[i], res.data.data);
        }
      });

      // 3. Fetch Template Names
      const uniqueTemplateIds = new Set<string>();
      clusters.forEach(cluster => {
        if (cluster.weekday_template_map) {
          Object.values(cluster.weekday_template_map).forEach(val => {
            if (val && val !== "None" && val.length > 5) {
              uniqueTemplateIds.add(val);
            }
          });
        }
      });

      const idToNameMap: Record<string, string> = {};

      if (uniqueTemplateIds.size > 0) {
        const templateIdsArray = Array.from(uniqueTemplateIds);
        const templatePromises = templateIdsArray.map(id =>
          api.appointment_api.get<SlotTemplateResponse>(`/api/slot-templates/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => null)
        );

        const templateResponses = await Promise.all(templatePromises);
        templateResponses.forEach((res, index) => {
          const originalId = templateIdsArray[index];
          if (res && res.data && res.data.template) {
            idToNameMap[originalId] = res.data.template.name;
          } else {
            idToNameMap[originalId] = originalId;
          }
        });
      }

      // 4. Combine Data
      const finalAppointmentsData = clusters.map((cluster) => {
        const centerItem = centerDataMap.get(cluster.center_id);

        let rawSlotDesigns: Record<string, string> = {};
        if (centerItem?.centerType === "CAMP CENTER") {
          rawSlotDesigns = { ...(cluster.weekday_template_map || {}) };
        } else {
          rawSlotDesigns = {
            Monday: "None", Tuesday: "None", Wednesday: "None",
            Thursday: "None", Friday: "None", Saturday: "None", Sunday: "None",
            ...(cluster.weekday_template_map || {})
          };
        }

        const namedSlotDesigns: Record<string, string> = {};
        Object.entries(rawSlotDesigns).forEach(([day, value]) => {
          if (idToNameMap[value]) {
            namedSlotDesigns[day] = idToNameMap[value];
          } else {
            namedSlotDesigns[day] = value;
          }
        });

        const totalSlots = cluster.total_slots || 0;
        const appointmentsPerSlot = cluster.appointments_per_slot || 0;

        return {
          id: cluster._id,
          appointmentId: cluster.appointment_cluster_id,
          centerId: cluster.center_id,
          centerName: centerItem?.name || "Unknown",
          centerType: centerItem?.centerType || "Unknown",
          city: centerItem?.city || "Unknown",
          startDate: cluster.start_date,
          endDate: cluster.end_date,
          slotDesigns: namedSlotDesigns,
          totalSlots,
          appointmentsPerSlot,
          totalAppointments: totalSlots * appointmentsPerSlot,
        };
      });

      setAppointments(finalAppointmentsData);
    } catch (err: any) {
      console.error("Failed to fetch appointments data:", err);
      setError(err.message || "Could not load data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAppointmentsData();
  }, [fetchAppointmentsData]);

  const uniqueCategories = useMemo(() => {
    const categories = appointments
      .map((appointment) => appointment.centerType)
      .filter((type): type is string => Boolean(type && type !== "Unknown"));
    return Array.from(new Set(categories));
  }, [appointments]);

  const uniqueCities = useMemo(() => {
    const cities = appointments
      .map((appointment) => appointment.city)
      .filter((city): city is string => Boolean(city && city !== "Unknown"));
    return Array.from(new Set(cities));
  }, [appointments]);

  // 🔽 2. Memoized filtering logic
  const filteredAppointments = useMemo(() => {
    return appointments.filter((appointment) => {
      const matchesSearch =
        !searchTerm ||
        appointment.centerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        appointment.city.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory =
        !selectedCategory || appointment.centerType === selectedCategory;

      const matchesCity =
        !selectedCity || appointment.city === selectedCity;

      return matchesSearch && matchesCategory && matchesCity;
    });
  }, [appointments, searchTerm, selectedCategory, selectedCity]);

  // 🔽 3. Reset page to 1 when search/filter values change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, selectedCity]);
  
  // 🔽 4. Pagination Logic
  const totalPages = Math.ceil(filteredAppointments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentAppointmentsToDisplay = filteredAppointments.slice(startIndex, endIndex);

  const handleViewClick = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAppointment(null);
  };
  const handleAppointmentUpdated = () => {
    fetchAppointmentsData();
  };

  // Edit Staff (EditClusterSlotsModal)
  const [isEditStaffModalOpen, setIsEditStaffModalOpen] = useState(false);
  const [editStaffAppointment, setEditStaffAppointment] = useState<Appointment | null>(null);

  const handleEditStaffClick = (appointment: Appointment) => {
    setEditStaffAppointment(appointment);
    setIsEditStaffModalOpen(true);
  };
  const handleCloseEditStaffModal = () => {
    setIsEditStaffModalOpen(false);
    setEditStaffAppointment(null);
  };

  // Move Appointments Modal
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  const [moveAppointment, setMoveAppointment] = useState<Appointment | null>(null);

  const handleMoveClick = (appointment: Appointment) => {
    setMoveAppointment(appointment);
    setIsMoveModalOpen(true);
  };
  const handleCloseMoveModal = () => {
    setIsMoveModalOpen(false);
    setMoveAppointment(null);
  };

  // Delete Cluster via Reschedule Config Modal
  const [isRescheduleConfigModalOpen, setIsRescheduleConfigModalOpen] = useState(false);
  const [clusterToDelete, setClusterToDelete] = useState<Appointment | null>(null);

  const handleDeleteClusterClick = (appointment: Appointment) => {
    setClusterToDelete(appointment);
    setIsRescheduleConfigModalOpen(true);
  };

  const handleCloseRescheduleConfigModal = () => {
    setIsRescheduleConfigModalOpen(false);
    setClusterToDelete(null);
  };

  if (loading) return <div className="edit-appointments-container"><Loader text="Loading Released Appointments..." /></div>;
  if (error) return <div className="edit-appointments-container"><p style={{ color: "red" }}>{error}</p></div>;

  return (
    <div className="edit-appointments-container">
      <div className="table-header">
        <h3 className="table-title">List of Appointments Released</h3>
        <div className="actions filter-controls-bar">
          <select
            className="center-filter-dropdown"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="">Center Category</option>
            {uniqueCategories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>

          <select
            className="center-filter-dropdown"
            value={selectedCity}
            onChange={(e) => setSelectedCity(e.target.value)}
          >
            <option value="">City</option>
            {uniqueCities.map((city) => (
              <option key={city} value={city}>
                {city}
              </option>
            ))}
          </select>

          <input
            type="text"
            placeholder="Search by Center Name"
            className="search-box"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>
      <table className="edit-appointments-table">
        <thead>
          <tr>
            <th>No.</th>
            <th>Center Name</th>
            <th>Center Type</th>
            <th>City</th>
            <th>Start Date / End Date</th>
            <th>Slots</th>
            <th>Appointments</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {currentAppointmentsToDisplay.length > 0 ? (
            currentAppointmentsToDisplay.map((row, index) => (
              <tr key={row.id}>
                <td>{startIndex + index + 1}</td>
                <td>{row.centerName}</td>
                <td><span className={`badge ${row.centerType.toLowerCase().includes("qavach") ? "qavach" : "camp"}`}>{row.centerType}</span></td>
                <td>{row.city}</td>
                <td>{row.startDate} / {row.endDate}</td>
                <td>{row.totalSlots}</td>
                <td>{row.totalAppointments}</td>
                <td className="action-btns-cell">
                  <button className="action-btn edit-slot-btn" onClick={() => handleViewClick(row)}>Edit Slot</button>
                  <button className="action-btn edit-staff-btn" onClick={() => handleEditStaffClick(row)}>Edit Staff</button>
                  <button className="action-btn move-appt-btn" onClick={() => handleMoveClick(row)}>Move Appointments</button>
                  <button className="action-btn delete-cluster-btn" onClick={() => handleDeleteClusterClick(row)}>Delete Cluster</button>
                </td>
              </tr>
            ))
          ) : (
            <tr><td colSpan={8} style={{ textAlign: 'center' }}>No appointments match your search.</td></tr>
          )}
        </tbody>
      </table>

      {/* 🔽 5. Dynamic Pagination Footer */}
      {totalPages > 0 && (
        <div className="pagination-footer">
            <p>Showing {startIndex + 1} to {Math.min(endIndex, filteredAppointments.length)} of {filteredAppointments.length} results</p>
            <div className="pagination">
                <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1}>Previous</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNumber => (
                    <button key={pageNumber} onClick={() => setCurrentPage(pageNumber)} className={currentPage === pageNumber ? "active" : ""}>{pageNumber}</button>
                ))}
                <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages}>Next</button>
            </div>
        </div>
      )}

      {isModalOpen && selectedAppointment && (
        <ViewEditAppointmentModal
          appointment={selectedAppointment}
          onClose={handleCloseModal}
          onAppointmentUpdated={handleAppointmentUpdated}
        />
      )}

      {isEditStaffModalOpen && editStaffAppointment && (
        <EditClusterSlotsModal
          station={editStaffAppointment}
          onClose={handleCloseEditStaffModal}
        />
      )}

      {isMoveModalOpen && moveAppointment && (
        <MoveAppointmentsModal
          sourceAppointment={moveAppointment}
          onClose={handleCloseMoveModal}
          onSuccess={handleAppointmentUpdated}
        />
      )}

      {isRescheduleConfigModalOpen && clusterToDelete && (
        <RescheduleConfigModal
          clusterToDelete={clusterToDelete}
          onClose={handleCloseRescheduleConfigModal}
          onSuccess={fetchAppointmentsData}
        />
      )}
    </div>
  );
};

export default EditAppointmentsTable;